import * as React from "react";
import { Check, Circle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface StageStepperProps {
  steps: { label: string }[];
  /** The step underway, counting from 1; earlier steps show as done. */
  current: number;
  /** What the current step is doing right now, under its label. */
  sublabel: string | undefined;
  /** "sm" fits over a slide-sized card. */
  size?: "default" | "sm";
  className?: string;
}

export const StageStepper: React.FC<StageStepperProps> = ({
  steps,
  current,
  sublabel,
  size = "default",
  className,
}) => {
  const sm = size === "sm";
  const icon = sm ? "size-3.5" : "size-4";
  return (
    <ol className={cn("flex flex-col", sm ? "gap-2" : "gap-3", className)} data-testid="stage-stepper">
      {steps.map(({ label }, index) => {
        const step = index + 1;
        const state = step < current ? "done" : step === current ? "current" : "pending";
        return (
          <li
            key={label}
            data-state={state}
            aria-current={state === "current" ? "step" : undefined}
            className={cn("flex items-start", sm ? "gap-2 text-xs" : "gap-2.5 text-sm")}
          >
            <span className={cn(sm ? "mt-px" : "mt-0.5", "shrink-0")}>
              {state === "done" && <Check className={cn(icon, "text-emerald-500")} aria-hidden="true" />}
              {state === "current" && (
                <Sparkles
                  className={cn(icon, "text-primary animate-pulse motion-reduce:animate-none")}
                  aria-hidden="true"
                />
              )}
              {state === "pending" && <Circle className={cn(icon, "text-slate-300")} aria-hidden="true" />}
            </span>
            <span className="flex flex-col gap-0.5">
              <span
                className={cn(
                  state === "pending" ? "text-slate-400" : "text-slate-700",
                  state === "current" && "font-semibold text-slate-900"
                )}
              >
                {label}
              </span>
              {state === "current" && sublabel && (
                <span className={cn("text-slate-500", sm ? "text-[11px]" : "text-xs")}>{sublabel}</span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
};
